import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, ShieldCheck } from "@phosphor-icons/react";

/**
 * §8a — Loop guard. Inbound mail from any address on this list is
 * silently dropped in `process_ms_message`, preventing bounces,
 * auto-responders, and our own machine-generated notifications from
 * spawning new tickets or endless reply chains.
 */
export function SystemAddressesPanel({ settings, setSettings, isAdmin }) {
  const [draft, setDraft] = useState("");
  const addresses = settings.system_addresses || [];

  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || !v.includes("@")) return;
    if (addresses.includes(v)) return;
    setSettings((s) => ({ ...s, system_addresses: [...(s.system_addresses || []), v].sort() }));
    setDraft("");
  };

  const remove = (addr) => {
    setSettings((s) => ({
      ...s,
      system_addresses: (s.system_addresses || []).filter((a) => a !== addr),
    }));
  };

  return (
    <section
      data-testid="system-addresses-panel"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <ShieldCheck weight="duotone" className="text-aurora-green" size={20} />
            Loop guard — system addresses
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Inbound mail from any address below is dropped without creating a
            ticket. Add bounce handlers, no-reply aliases, DMARC report
            senders, and any automation that would otherwise spawn a loop.
          </p>
        </div>
      </header>

      <div className="mt-5 flex flex-wrap gap-2">
        {addresses.length === 0 && (
          <p className="text-sm text-slate-500">
            No addresses configured yet — inbound mail from every sender will
            be processed normally.
          </p>
        )}
        {addresses.map((addr) => (
          <span
            key={addr}
            data-testid={`system-address-chip-${addr}`}
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-800"
          >
            {addr}
            {isAdmin && (
              <button
                type="button"
                onClick={() => remove(addr)}
                data-testid={`system-address-remove-${addr}`}
                aria-label={`Remove ${addr}`}
                className="text-slate-500 hover:text-rose-600"
              >
                <X size={14} weight="bold" />
              </button>
            )}
          </span>
        ))}
      </div>

      {isAdmin && (
        <div className="mt-5 flex gap-2">
          <Input
            data-testid="system-address-input"
            placeholder="postmaster@example.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            className="max-w-sm"
          />
          <Button
            type="button"
            data-testid="system-address-add-btn"
            onClick={add}
            disabled={!draft.trim() || !draft.includes("@")}
          >
            Add address
          </Button>
        </div>
      )}
    </section>
  );
}
